// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/SuggestedToolModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input, Segmented } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SuggestedToolModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_tool') : translateX('new_tool');

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          name: initialData.name || '',
          type: initialData.type || 'physical',
          description: initialData.description || '',
          link: initialData.link || '',
        });
      } else {
        form.resetFields();
      }

      // auto-focus on name field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({
        name: (values.name || '').trim(),
        type: values.type || 'physical',
        description: (values.description || '').trim(),
        link: (values.link || '').trim(),
      });
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={450}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ name: '', type: 'physical', description: '', link: '' }}
      >
        <Form.Item
          name="name"
          label={translateX('name')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={60}
            placeholder={translateX('name')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label={translateX('type')}
        >
          <Segmented
            options={[
              { label: translateX('physical_tool'), value: 'physical' },
              { label: translateX('software_tool'), value: 'software' },
            ]}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={translateX('description')}
        >
          <Input
            maxLength={120}
            placeholder={translateX('description')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="link"
          label={translateX('link')}
        >
          <Input
            placeholder="https://..."
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SuggestedToolModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
